import { useNavigate } from 'react-router-dom';
import { Command, Keyboard, LogOut, Monitor, Moon, Rows3, Rows4, Sun, User } from 'lucide-react';
import {
  AppShellHeader,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Kbd,
  Tooltip,
  UserAvatar,
} from '@shining-technologies/ui';
import { useUiStore } from '@/stores/ui';
import { useTheme, type ThemePref } from '@/hooks/useTheme';
import { useAuth } from '@/features/auth/AuthProvider';
import { useLogout } from '@/features/auth/queries';
import { formatRoleLabel } from '@/lib/format';
import { absoluteApiUrl } from '@/lib/api';
import { MobileNavTrigger } from './Sidebar';

const THEME_PREFS: readonly ThemePref[] = ['light', 'dark', 'system'];

function isThemePref(v: string): v is ThemePref {
  return (THEME_PREFS as readonly string[]).includes(v);
}

/** The app header: the kit `AppShellHeader` (sticky, below any impersonation banner). */
export function Topbar() {
  const setCommandOpen = useUiStore((s) => s.setCommandPaletteOpen);
  const setShortcutsOpen = useUiStore((s) => s.setShortcutsOpen);
  const density = useUiStore((s) => s.density);
  const setDensity = useUiStore((s) => s.setDensity);
  const { pref, setPref } = useTheme();
  const { user } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();

  return (
    <AppShellHeader
      // Sits below the impersonation banner when one is shown — see
      // FIX(admin-shell-banner-offset) in ImpersonationBanner.
      className="top-(--admin-banner-h,0px)"
      start={
        <>
          {/* FIX(admin-mobile-shell): the nav drawer trigger (phones only). */}
          <MobileNavTrigger />
          {/* FIX(admin-command-palette): Before: below `sm` the label is hidden, so
              the button was an unnamed icon. After: always named "Search". */}
          <Button
            variant="outline"
            size="sm"
            className="text-muted-foreground"
            aria-label="Search"
            onClick={() => setCommandOpen(true)}
          >
            <Command aria-hidden />
            <span className="hidden sm:inline">Search…</span>
            <Kbd className="ml-2 hidden sm:inline-flex">⌘K</Kbd>
          </Button>
        </>
      }
      end={
        <>
          <Tooltip content={`Density: ${density}`}>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Toggle density"
              onClick={() => setDensity(density === 'dense' ? 'comfortable' : 'dense')}
            >
              {density === 'dense' ? <Rows4 aria-hidden /> : <Rows3 aria-hidden />}
            </Button>
          </Tooltip>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Theme">
                {pref === 'dark' ? <Moon aria-hidden /> : pref === 'light' ? <Sun aria-hidden /> : <Monitor aria-hidden />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuRadioGroup value={pref} onValueChange={(v) => {
                  if (isThemePref(v)) setPref(v);
                }}
              >
                <DropdownMenuRadioItem value="light">
                  <Sun aria-hidden /> Light
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="dark">
                  <Moon aria-hidden /> Dark
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="system">
                  <Monitor aria-hidden /> System
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="ghost" size="icon" aria-label="Keyboard shortcuts" onClick={() => setShortcutsOpen(true)}>
            <Keyboard aria-hidden />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Account menu">
                <UserAvatar
                  name={user?.name || '?'}
                  seed={user?.id}
                  src={user?.avatarUrl ? absoluteApiUrl(user.avatarUrl) : undefined}
                  aria-hidden
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="truncate">{user?.name}</span>
                  <span className="truncate text-xs font-normal text-muted-foreground">{user?.email}</span>
                  {/* FIX(admin-command-palette): the label of the REAL server role slug (a
                      display label only — never authority). */}
                  {user && (
                    <Badge tone="neutral" className="mt-1 w-fit">
                      {formatRoleLabel(user.serverRole)}
                    </Badge>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigate('/settings')}>
                <User aria-hidden /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => logout.mutate()}>
                <LogOut aria-hidden /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      }
    />
  );
}
